import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Link2 } from "lucide-react";
import api from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Skeleton } from "@/components/ui/skeleton";
import { AudioWrap, RecapCard, SampleBlock, SourceLine } from "@/components/WrapReport";

function ShareLink({ slug }) {
  return (
    <span data-testid={`wrap-share-path-${slug}`} className="flex items-center gap-1.5 text-xs text-neutral-500">
      <Link2 className="h-3.5 w-3.5" /> Public link: <span className="font-mono">{window.location.origin}/share/wrap/{slug}</span>
    </span>
  );
}

function Overview({ d }) {
  return (
    <>
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Wrap Reports</h1>
      <div className="mt-2"><SourceLine source={d.source} note={d.not_combined_note} /></div>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {d.audios.map((a) => (
          <Link key={a.slug} to={`/wraps/${a.slug}`} data-testid={`wrap-card-${a.slug}`} className="pb-card group p-5 transition-colors hover:bg-neutral-50">
            <p className="text-xs text-neutral-500">{a.artist}</p>
            <p className="font-display text-xl font-bold">{a.title}</p>
            <p className="text-xs text-neutral-500">{a.period}</p>
            <p className="num-display mt-3 text-2xl font-extrabold">{a.headline.views}</p>
            <p className="text-xs text-neutral-500">views · {a.headline.likes} likes · {a.headline.saves_shares} saves+shares</p>
            <ChevronRight className="mt-2 h-4 w-4 text-neutral-300 group-hover:text-neutral-900" />
          </Link>
        ))}
      </div>
      <div className="mt-8" id="recap"><RecapCard recap={d.recap} source={d.source} note={d.not_combined_note} /></div>
      <div className="mt-2"><ShareLink slug="recap" /></div>
    </>
  );
}

function Detail({ d, slug }) {
  const audio = d.audios.find((a) => a.slug === slug);
  if (!audio) return <p data-testid="wrap-not-found" className="text-neutral-500">Wrap report not found.</p>;
  return (
    <>
      <div className="mb-4"><ShareLink slug={slug} /></div>
      <AudioWrap audio={audio} source={d.source} note={d.not_combined_note} />
      {slug === "immaculate" && (
        <div className="mt-8">
          <SampleBlock sample={d.immaculate_sample} owner />
          <div className="mt-2"><ShareLink slug="immaculate-sample" /></div>
        </div>
      )}
    </>
  );
}

export default function Wraps() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/wraps").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  useEffect(() => {
    if (d && window.location.hash) document.querySelector(window.location.hash)?.scrollIntoView();
  }, [d, slug]);
  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {slug && <Link to="/wraps" data-testid="wrap-back-btn" className="mb-6 flex items-center gap-1.5 text-sm text-neutral-500 hover:text-black"><ArrowLeft className="h-4 w-4" /> All wrap reports</Link>}
        {err && <p data-testid="wrap-error" className="text-neutral-500">Could not load wrap reports.</p>}
        {!d && !err && <Skeleton className="h-64 rounded-2xl" />}
        {d && (slug ? <Detail d={d} slug={slug} /> : <Overview d={d} />)}
      </main>
    </div>
  );
}
